import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchClients, 
  fetchClientProjects, 
  createClient,
  updateClient,
  deleteClient 
} from '../../store/slices/clientSlice';
import axios from 'axios';
import InvoiceForm from '../Invoice/InvoiceForm';
import invoiceService from '../../services/invoiceService';
import { toast } from '@/components/UI/use-toast';
import { Pencil, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/UI/select";
import InvoiceFileBrowser from '@/components/Dashboard/InvoiceFileBrowser';
import { Building2, Files, Clock, Plus, Loader2, Upload, FileText, FolderOpen } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/UI/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/UI/card";
import { Button } from "@/components/UI/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/UI/dialog";
import { Input } from "@/components/UI/input";
import InvoiceUploadDialog from '@/components/Dashboard/InvoiceUploadDialog';

import StatementOfAccount from './StatementOfAccount';


// Constants
const FLASK_API_URL = 'https://accountss-loams.onrender.com';



// Inside the Dashboard component, add this handler








const EditClientDialog = ({ open, onOpenChange, client }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const selectedClientProjects = useSelector(state => state.clients.selectedClientProjects);
  const [formData, setFormData] = useState({
    project_code: '',
    aux: '',
    client_name: '',
    project_name: '',
    loa_wo_sub_contract_ref: '',
    contract_value: '',
    payment_terms: '30 Days',
    advance_payment_received: '',
    trn: '',
    address: '',
    telephone_no: '',
    fax_no: '',
    bank_name_branch: 'ABU DHABI COMMERCIAL BANK PJSC, Dubai, UAE',
    account_number: '10412895920001',
    iban: 'AE460030010412895920001',
    swift: 'ADCBAEAA'
  });

  // Fetch projects when dialog opens
  useEffect(() => {
    if (client && open) {
      dispatch(fetchClientProjects(client.client_name));
    }
  }, [client, open, dispatch]);

  // Update form data when selected project changes
  const handleProjectCodeChange = (projectCode) => {
    const selectedProject = selectedClientProjects.find(p => p.project_code === projectCode);
    if (selectedProject) {
      setFormData({
        project_code: selectedProject.project_code,
        aux: selectedProject.aux || '',
        client_name: selectedProject.client_name,
        project_name: selectedProject.project_name,
        advance_payment_received:selectedProject.advance_payment_received,
        loa_wo_sub_contract_ref: selectedProject.loa_wo_sub_contract_ref,
        contract_value: selectedProject.contract_value,
        payment_terms: selectedProject.payment_terms,
        trn: selectedProject.trn,
        address: selectedProject.address || '',
        telephone_no: selectedProject.telephone_no || '',
        fax_no: selectedProject.fax_no || '',
        bank_name_branch: selectedProject.bank_name_branch,
        account_number: selectedProject.account_number,
        iban: selectedProject.iban,
        swift: selectedProject.swift
      });
    }
  };

  // Set initial data when client changes or projects are loaded
  useEffect(() => {
    if (selectedClientProjects.length === 1) {
      handleProjectCodeChange(selectedClientProjects[0].project_code);
    }
  }, [selectedClientProjects]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
  
    try {
      await dispatch(updateClient({
        projectCode: formData.project_code,
        clientData: formData
      })).unwrap();
  
      toast({
        title: "Success",
        description: "Client updated successfully",
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update client",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Client</DialogTitle>
          <DialogDescription>
            Update the client and project details below
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Project Code*</label>
              {selectedClientProjects.length > 1 ? (
                <Select 
                  value={formData.project_code}
                  onValueChange={handleProjectCodeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project code" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedClientProjects.map(project => (
                      <SelectItem 
                        key={project.project_code} 
                        value={project.project_code}
                      >
                        {project.project_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={formData.project_code}
                  disabled
                  className="bg-gray-100"
                />
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Auxiliary Number</label>
              <Input
                name="aux"
                value={formData.aux}
                onChange={handleChange}
                placeholder="e.g., 1985"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Client Name*</label>
              <Input
                required
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Project Name*</label>
              <Input
                required
                name="project_name"
                value={formData.project_name}
                onChange={handleChange}
              />
            </div>

            {/* Continue with other fields... */}
            <div className="space-y-2">
              <label className="text-sm font-medium">LOA/WO Reference*</label>
              <Input
                required
                name="loa_wo_sub_contract_ref"
                value={formData.loa_wo_sub_contract_ref}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Contract Value*</label>
              <Input
                required
                type="number"
                name="contract_value"
                value={formData.contract_value}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Advance Paymnet Received*</label>
              <Input
                required
                type="number"
                name="advance_payment_received"
                value={formData.advance_payment_received}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">TRN*</label>
              <Input
                required
                name="trn"
                value={formData.trn}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Address</label>
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Telephone</label>
              <Input
                name="telephone_no"
                value={formData.telephone_no}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fax</label>
              <Input
                name="fax_no"
                value={formData.fax_no}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Account Number*</label>
              <Input
                required
                name="account_number"
                value={formData.account_number}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">IBAN*</label>
              <Input
                required
                name="iban"
                value={formData.iban}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Client'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};




// Create Client Dialog Component
const CreateClientDialog = ({ open, onOpenChange }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    project_code: '',
    aux: '',
    client_name: '',
    project_name: '',
    loa_wo_sub_contract_ref: '',
    contract_value: '',
    payment_terms: '30 Days',
    advance_payment_received: '',
    trn: '',
    address: '',
    telephone_no: '',
    fax_no: '',
    bank_name_branch: 'ABU DHABI COMMERCIAL BANK PJSC, Dubai, UAE',
    account_number: '',
    iban: '',
    swift: 'ADCBAEAA'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await dispatch(createClient(formData)).unwrap();
      toast({
        title: "Success",
        description: "Client created successfully",
      });
      onOpenChange(false);
      // Reset form
      setFormData({
        project_code: '',
        aux: '',
        client_name: '',
        project_name: '',
        loa_wo_sub_contract_ref: '',
        contract_value: '',
        advance_payment_received: '',
        payment_terms: '30 Days',
        trn: '',
        address: '',
        telephone_no: '',
        fax_no: '',
        bank_name_branch: 'ABU DHABI COMMERCIAL BANK PJSC, Dubai, UAE',
        account_number: '',
        iban: '',
        swift: 'ADCBAEAA'
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create client",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Client</DialogTitle>
          <DialogDescription>
            Enter the client and project details below
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Project Code*</label>
              <Input
                required
                name="project_code"
                value={formData.project_code}
                onChange={handleChange}
                placeholder="PJ2210"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Auxiliary Number</label>
              <Input
                name="aux"
                value={formData.aux}
                onChange={handleChange}
                placeholder="1985"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Advance Payment Recived</label>
              <Input
                name="advance_payment_received"
                value={formData.advance_payment_received}
                onChange={handleChange}
                placeholder="xxxxxx"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Client Name*</label>
              <Input
                required
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                placeholder="Company Name"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Project Name*</label>
              <Input
                required
                name="project_name"
                value={formData.project_name}
                onChange={handleChange}
                placeholder="Project Description"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">LOA/WO Reference*</label>
              <Input
                required
                name="loa_wo_sub_contract_ref"
                value={formData.loa_wo_sub_contract_ref}
                onChange={handleChange}
                placeholder="WO.Ref: XXX-XXX"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Contract Value*</label>
              <Input
                required
                type="number"
                name="contract_value"
                value={formData.contract_value}
                onChange={handleChange}
                placeholder="850000.00"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">TRN*</label>
              <Input
                required
                name="trn"
                value={formData.trn}
                onChange={handleChange}
                placeholder="100023353400003"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Address</label>
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="P.O. Box XXXXX, Dubai, UAE"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Telephone</label>
              <Input
                name="telephone_no"
                value={formData.telephone_no}
                onChange={handleChange}
                placeholder="+971 X XXX XXXX"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fax</label>
              <Input
                name="fax_no"
                value={formData.fax_no}
                onChange={handleChange}
                placeholder="+971 X XXX XXXX"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Account Number*</label>
              <Input
                required
                name="account_number"
                value={formData.account_number}
                onChange={handleChange}
                placeholder="XXXXXXXXXX"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">IBAN*</label>
              <Input
                required
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder="AEXXXXXXXXXXXXXXXXXX"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Client'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Client Card Component
// Update the ClientCard component
const ClientCard = ({ client, isSelected, onClick, onEdit, onDelete }) => (
  <Card 
    className={`cursor-pointer transition-colors ${
      isSelected ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
    }`}
  >
    <CardContent className="p-4">
      <div className="flex justify-between items-start">
        <div onClick={onClick} className="flex-1">
          <h3 className="font-medium text-lg">{client.client_name}</h3>
          <div className="mt-2 space-y-1 text-sm text-gray-600">
            <p>TRN: {client.trn}</p>
            {client.address && <p className="text-xs">{client.address}</p>}
            {client.telephone_no && <p className="text-xs">Tel: {client.telephone_no}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(client);
            }}
            className="ml-2"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(client);
            }}
            className="ml-2 text-red-600 hover:text-red-800"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Project Card Component
const ProjectCard = ({ project, onClick }) => (
  <Card 
    className="cursor-pointer hover:bg-gray-50 transition-colors"
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          <span className="inline-block px-2 py-1 text-xs font-semibold bg-indigo-100 text-indigo-800 rounded-full">
            {project.project_code}
          </span>
          {project.aux && (
            <span className="text-xs text-gray-500">AUX: {project.aux}</span>
          )}
        </div>
        
        <h3 className="font-medium">{project.project_name}</h3>
        
        <div className="text-sm text-gray-600 space-y-1">
          <p>LOA/WO Ref: {project.loa_wo_sub_contract_ref}</p>
          <p>Contract Value: {new Intl.NumberFormat('en-AE', {
            style: 'currency',
            currency: 'AED'
          }).format(project.contract_value)}</p>
          <p>Advance payment recived: {new Intl.NumberFormat('en-AE', {
            style: 'currency',
            currency: 'AED'
          }).format(project.advance_payment_received)}</p>

          <p>Payment Terms: {project.payment_terms}</p>

        </div>

        <div className="text-xs text-gray-500 space-y-1">
          <p>Bank: {project.bank_name_branch}</p>
          <p>Account: {project.account_number}</p>
          <p>IBAN: {project.iban}</p>
          <p>SWIFT: {project.swift}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Invoice Card Component
const InvoiceCard = ({ invoice, onView, onEdit, onDownload }) => (
  <Card className="hover:bg-gray-50 transition-colors">
    <CardContent className="p-4">
      <div className="space-y-2">
        <div className="flex justify-between items-start">
          <h4 className="font-medium">Invoice #{invoice.serial_number}</h4>
          {invoice.tax_invoice_ref && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
              Tax Ref: {invoice.tax_invoice_ref}
            </span>
          )}
        </div>

        <p className="text-sm text-gray-600">Project: {invoice.project_code}</p>
        <p className="text-sm text-gray-600">
          Amount: AED {invoice.amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
        </p>
        <p className="text-sm text-gray-500">
          Date: {new Date(invoice.tax_invoice_date || invoice.uploaded_at).toLocaleDateString()}
        </p>
        
        <div className="flex gap-2 mt-2">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onView(invoice);
            }}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onEdit(invoice);
            }}
            className="text-sm text-green-600 hover:text-green-800"
          >
            Edit
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onDownload(invoice);
            }}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Download
          </button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Main Dashboard Component
const Dashboard = () => {
  const dispatch = useDispatch();
  const { list: clients, selectedClientProjects, loading, error } = useSelector(state => state.clients);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeTab, setActiveTab] = useState("clients");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
const [editingClient, setEditingClient] = useState(null);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);


  useEffect(() => {
    dispatch(fetchClients());
  }, [dispatch]);

  const fetchClientInvoices = async (clientName) => {
    try {
      setLoadingInvoices(true);
      const response = await axios.get(
        `${FLASK_API_URL}/api/invoices/client/${encodeURIComponent(clientName)}`,
        {
          headers: {
            'Authorization': localStorage.getItem('token'),
            'Accept': 'application/json'
          }
        }
      );
  
      if (response.data.status === 'success') {
        const invoices = response.data.invoices.map(invoice => ({
          ...invoice,
          file_path: invoice.file_path || `invoices/${clientName}/${invoice.serial_number}_${invoice.created_at.split('T')[0]}.pdf`
        }));
        setRecentInvoices(invoices);
      } else {
        console.error('Failed to fetch invoices:', response.data.message);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };




  const handleDeleteClient = async (client) => {
    try {
      // Get projects for this client if not already loaded
      let projectCode;
      if (selectedClientProjects.length === 0) {
        const projectsResponse = await dispatch(fetchClientProjects(client.client_name)).unwrap();
        projectCode = projectsResponse[0]?.project_code;
      } else {
        projectCode = selectedClientProjects[0]?.project_code;
      }

      if (!projectCode) {
        toast({
          title: "Error",
          description: "Could not find project code for this client",
          variant: "destructive"
        });
        return;
      }

      // Confirm before deleting
      if (!window.confirm(`Are you sure you want to delete ${client.client_name}?`)) {
        return;
      }

      await dispatch(deleteClient(projectCode)).unwrap();
      
      toast({
        title: "Success",
        description: "Client deleted successfully",
      });
      
      // Refresh the clients list
      dispatch(fetchClients());
      
      // Reset selections if the deleted client was selected
      if (selectedClient?.client_name === client.client_name) {
        setSelectedClient(null);
        setSelectedProject(null);
        setShowInvoice(false);
        setActiveTab("clients");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete client",
        variant: "destructive"
      });
    }
  };






  const handleClientClick = async (client) => {
    setSelectedClient(client);
    setSelectedProject(null);
    setShowInvoice(false);
    await dispatch(fetchClientProjects(client.client_name));
    fetchClientInvoices(client.client_name);
    setActiveTab("projects");
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setShowInvoice(true);
    setActiveTab("invoices");
  };


  // Add this handler function
const handleEditClient = (client) => {
  setEditingClient(client);
  setEditDialogOpen(true);
};

  const handleBackToList = () => {
    setShowInvoice(false);
    setSelectedProject(null);
    setActiveTab("projects");
    if (selectedClient) {
      fetchClientInvoices(selectedClient.client_name);
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const viewUrl = await invoiceService.getInvoiceUrl(invoice.file_path);
      const link = document.createElement('a');
      link.href = viewUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to view invoice. Please try downloading instead.",
        variant: "destructive"
      });
    }
  };

  const handleEditInvoice = async (invoice) => {
    try {
      const invoiceResponse = await axios.get(
        `${FLASK_API_URL}/api/invoices/${invoice.id}`,
        {
          headers: {
            'Authorization': localStorage.getItem('token'),
            'Accept': 'application/json'
          }
        }
      );

      if (!invoiceResponse.data) {
        throw new Error('Failed to fetch invoice details');
      }

      const invoiceData = invoiceResponse.data;
      const project = selectedClientProjects.find(p => p.project_code === invoice.project_code);
      
      if (!project) {
        throw new Error('Project not found');
      }

      const projectWithInvoice = {
        ...project,
        editingInvoice: {
          ...invoiceData,
          bank: invoiceData.bank_name_branch?.includes('ADCB') ? 'ADCB' : 'INVEST',
          accountName: 'Phoenician Technical Services LLC',
          accountNumber: invoiceData.account_number || project.account_number,
          iban: invoiceData.iban || project.iban,
          swift: invoiceData.swift || project.swift
        }
      };

      setSelectedProject(projectWithInvoice);
      setShowInvoice(true);
      setActiveTab("invoices");

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load invoice for editing. " + error.message,
        variant: "destructive"
      });
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      const viewResponse = await axios.get(
        `${FLASK_API_URL}/api/documents/view?path=${encodeURIComponent(invoice.file_path)}`,
        {
          headers: {
            'Authorization': localStorage.getItem('token'),
            'Accept': 'application/json'
          }
        }
      );

      if (!viewResponse.data.url) {
        throw new Error('Failed to get download URL');
      }

      const downloadResponse = await axios.get(viewResponse.data.url, {
        responseType: 'blob'
      });

      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.serial_number || 'invoice'}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download invoice. Please try again.",
        variant: "destructive"
      });
    }
  };

  const renderClientsTab = () => (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-700">Clients</h2>
          <Button onClick={() => setCreateDialogOpen(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create Client
          </Button>
        </div>
        
        {loading ? (
          <div className="relative min-h-[200px]">
            <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative">
                  {/* Outer spinning ring */}
                  <div className="w-12 h-12 border-3 border-primary rounded-full animate-spin border-t-transparent" />
                  {/* Inner pulsing circle */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 bg-primary/20 rounded-full animate-pulse" />
                  </div>
                </div>
                <span className="text-sm font-medium text-muted-foreground">
                  Loading clients
                  <span className="animate-pulse">...</span>
                </span>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="text-red-500 py-4">{error}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients?.map((client) => (
              <ClientCard
                key={client.client_name}
                client={client}
                isSelected={selectedClient?.client_name === client.client_name}
                onClick={() => handleClientClick(client)}
                onEdit={handleEditClient}
                onDelete={() => handleDeleteClient(client)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderProjectsTab = () => (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="p-4">
        {!selectedClient ? (
          <div className="text-center py-4 text-gray-500">
            Select a client to view projects
          </div>
        ) : selectedClientProjects.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No projects found for this client
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedClientProjects.map((project) => (
              <ProjectCard
                key={project.project_code}
                project={project}
                onClick={() => handleProjectClick(project)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderInvoicesTab = () => (
    showInvoice && selectedProject && selectedClient ? (
      <InvoiceForm 
        client={selectedClient}
        project={selectedProject}
        onBack={handleBackToList}
        onInvoiceSaved={async () => {
          await fetchClientInvoices(selectedClient.client_name);
          handleBackToList();
        }}
      />
    ) : (
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-4">
          {!selectedClient ? (
            <div className="text-center py-4 text-gray-500">
              Select a client to view recent invoices
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">
                  Recent Invoices
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setUploadDialogOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Existing Invoice
                  </Button>
                </div>
              </div>
              
              {loadingInvoices ? (
                <div className="text-center py-4">Loading invoices...</div>
              ) : recentInvoices.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No recent invoices found
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Recent Invoices Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {recentInvoices.map((invoice) => (
                      <InvoiceCard
                        key={invoice.id}
                        invoice={invoice}
                        onView={handleViewInvoice}
                        onEdit={handleEditInvoice}
                        onDownload={handleDownloadInvoice}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  );

  return (
    <div className="w-full p-4">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full grid-cols-5">
  <TabsTrigger 
    value="clients" 
    className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
  >
    <Building2 className="w-4 h-4" />
    Clients
  </TabsTrigger>
  <TabsTrigger 
    value="projects" 
    className="flex items-center gap-2 data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
  >
    <Files className="w-4 h-4" />
    Projects {selectedClient && `(${selectedClient.client_name})`}
  </TabsTrigger>
  <TabsTrigger 
    value="invoices" 
    className="flex items-center gap-2 data-[state=active]:bg-green-600 data-[state=active]:text-white"
  >
    <Clock className="w-4 h-4" />
    Invoices
  </TabsTrigger>
  <TabsTrigger 
    value="files" 
    className="flex items-center gap-2 data-[state=active]:bg-orange-600 data-[state=active]:text-white"
  >
    <FolderOpen className="w-4 h-4" />
    Invoice Files
  </TabsTrigger>
  <TabsTrigger 
    value="soa" 
    className="flex items-center gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white"
  >
    <FileText className="w-4 h-4" />
    Statement of Account
  </TabsTrigger>
</TabsList>

        <TabsContent value="clients" className="mt-4">
          {renderClientsTab()}
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          {selectedClient && (
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-700">
                Projects for {selectedClient.client_name}
              </h2>
            </div>
          )}
          {renderProjectsTab()}
        </TabsContent>


        <TabsContent value="soa" className="mt-4">
  <StatementOfAccount invoiceService={invoiceService} />
</TabsContent>



        <TabsContent value="invoices" className="mt-4">
          {selectedClient && !showInvoice && (
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-700">
                Invoices for {selectedClient.client_name}
              </h2>
            </div>
          )}
          {renderInvoicesTab()}
        </TabsContent>

        <TabsContent value="files" className="mt-4">
  <InvoiceFileBrowser />
</TabsContent>
      </Tabs>

      

      <div className="mt-4 text-sm text-gray-600">
        <nav className="flex items-center space-x-2">
          <span 
            className={`cursor-pointer hover:text-blue-600 ${
              activeTab === 'clients' ? 'text-blue-600 font-medium' : ''
            }`}
            onClick={() => {
              setActiveTab('clients');
              setSelectedClient(null);
              setSelectedProject(null);
              setShowInvoice(false);
            }}
          >
            Clients
          </span>
          {selectedClient && (
            <>
              <span className="text-gray-400">/</span>
              <span 
                className={`cursor-pointer hover:text-blue-600 ${
                  activeTab === 'projects' ? 'text-blue-600 font-medium' : ''
                }`}
                onClick={() => {
                  setActiveTab('projects');
                  setSelectedProject(null);
                  setShowInvoice(false);
                }}
              >
                {selectedClient.client_name}
              </span>
            </>
          )}
          {selectedProject && (
            <>
              <span className="text-gray-400">/</span>
              <span 
                className={`cursor-pointer hover:text-blue-600 ${
                  activeTab === 'invoices' ? 'text-blue-600 font-medium' : ''
                }`}
              >
                {selectedProject.project_code}
              </span>
            </>
          )}
        </nav>
      </div>

      <CreateClientDialog 
        open={createDialogOpen} 
        onOpenChange={setCreateDialogOpen} 
      />



      {/* Add this */}
      <InvoiceUploadDialog 
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        client={selectedClient}
        onSuccess={() => {
          fetchClientInvoices(selectedClient.client_name);
        }}
      />
      <EditClientDialog 
  open={editDialogOpen}
  onOpenChange={setEditDialogOpen}
  client={editingClient}
/>
    


    </div>
  );
};

export default Dashboard;